import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight, Loader2, Wand2, Check } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import { formatMoney } from '@/src/lib/format';

// Weekly sales forecast: per service and day, bookings, covers and $ per head (inc GST).
// Saved to venue_forecasts and used by the weekly report (forecast vs actual) and labour targets.
type Svc = { bookings: (number | null)[]; covers: (number | null)[]; sph: (number | null)[] };
type Data = { services: Record<string, Svc>; labourTarget?: number | null };

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (s: string, n: number) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const mondayOf = (d = new Date()) => { const x = new Date(d); x.setHours(12); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return iso(x); };
const empty = (): Svc => ({ bookings: Array(7).fill(null), covers: Array(7).fill(null), sph: Array(7).fill(null) });
const n = (v: unknown) => (typeof v === 'number' && isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && isFinite(+v) ? +v : null);

export default function ForecastModal({ isOpen, onClose, venueId, userId, inline = false }: { isOpen: boolean; onClose: () => void; venueId: string; userId?: string; /** Show as a page section (Financials › Forecast) instead of a popup. */ inline?: boolean }) {
  const c = useAppColors();
  const [week, setWeek] = useState(() => addDays(mondayOf(), 7));
  const [services, setServices] = useState<string[]>(['Lunch', 'Dinner']);
  const [data, setData] = useState<Data>({ services: {} });
  const [sched, setSched] = useState<number[]>(Array(7).fill(0));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [why, setWhy] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    let off = false;
    setLoading(true); setErr(null); setDirty(false); setSaving('idle'); setWhy([]);
    void (async () => {
      const [{ data: cfg }, { data: fc, error }, { data: sh }] = await Promise.all([
        supabase.from('eod_settings').select('config').eq('venue_id', venueId).maybeSingle(),
        supabase.from('venue_forecasts').select('data').eq('venue_id', venueId).eq('week_start', week).maybeSingle(),
        // Includes drafts: the roster is planned against the forecast before it's published.
        supabase.from('shifts').select('start_time, cost_estimate').eq('venue_id', venueId)
          .gte('start_time', `${addDays(week, -1)}T00:00:00`).lt('start_time', `${addDays(week, 8)}T00:00:00`),
      ]);
      if (off) return;
      const svcs: string[] = Array.isArray(cfg?.config?.services) && cfg.config.services.length ? cfg.config.services : ['Lunch', 'Dinner'];
      setServices(svcs);
      if (error) setErr(/venue_forecasts|schema cache/.test(error.message) ? 'Forecasts need the latest database update (20261028070000_weekly_reports.sql).' : error.message);
      const d = ((fc?.data ?? {}) as Data);
      const out: Data = { services: {}, labourTarget: d.labourTarget ?? null };
      for (const s of svcs) out.services[s] = { ...empty(), ...(d.services?.[s] ?? {}) };
      setData(out);
      const cost = Array(7).fill(0);
      for (const r of (sh ?? []) as Array<{ start_time: string; cost_estimate: number | null }>) {
        const i = Math.round((new Date(`${iso(new Date(r.start_time))}T12:00:00`).getTime() - new Date(`${week}T12:00:00`).getTime()) / 864e5);
        if (i >= 0 && i < 7) cost[i] += n(r.cost_estimate) ?? 0;
      }
      setSched(cost);
      setLoading(false);
    })();
    return () => { off = true; };
  }, [isOpen, venueId, week]);

  const set = (s: string, k: keyof Svc, i: number, v: string) => {
    setData((d) => { const x = { ...d.services[s], [k]: [...d.services[s][k]] }; x[k][i] = v === '' ? null : +v; return { ...d, services: { ...d.services, [s]: x } }; });
    setDirty(true); setSaving('idle');
  };
  const rev = (s: string, i: number) => { const x = data.services[s]; const cv = x?.covers[i]; const p = x?.sph[i]; return cv != null && p != null ? cv * p : null; };
  const dayRev = useMemo(() => DAYS.map((_, i) => services.reduce((a, s) => a + (rev(s, i) ?? 0), 0)), [data, services]); // eslint-disable-line react-hooks/exhaustive-deps
  const weekRev = dayRev.reduce((a, b) => a + b, 0);
  const weekCovers = services.reduce((a, s) => a + (data.services[s]?.covers ?? []).reduce<number>((x, y) => x + (y ?? 0), 0), 0);
  const schedTotal = sched.reduce((a, b) => a + b, 0);
  const tax = 0.1;
  const labourPct = (lab: number, r: number) => (r > 0 && lab > 0 ? Math.round((lab / (r / (1 + tax))) * 1000) / 10 : null);

  // Suggested forecast for each day:
  //  - the last 4 of the same weekday (e.g. the last 4 Tuesdays), and
  //  - the same weekday a year ago (52 weeks back), scaled by how the last 4 weeks compare with the same 4 weeks last year.
  //  Blend: 70% last 4 weeks, 30% last year. Public holidays use last year's same holiday when there is one.
  //  The split between services and $ per head come from the last 4 weeks.
  const fillFromHistory = async () => {
    setErr(null);
    const ly = (iso: string) => addDays(iso, -364);
    const [{ data: rows, error }, { data: lyRows }, { data: v }] = await Promise.all([
      supabase.from('sales_days').select('trade_date, covers, total_sales, eod').eq('venue_id', venueId).gte('trade_date', addDays(week, -28)).lt('trade_date', week),
      supabase.from('sales_days').select('trade_date, covers, total_sales, eod').eq('venue_id', venueId).gte('trade_date', ly(addDays(week, -28))).lte('trade_date', ly(addDays(week, 6))),
      supabase.from('venues').select('state').eq('id', venueId).maybeSingle(),
    ]);
    if (error) { setErr(error.message); return; }
    type Row = { trade_date: string; covers: number | null; total_sales: number | null; eod: { calc?: { covers?: unknown; total?: unknown; services?: Record<string, { sales?: unknown; covers?: unknown }> } } | null };
    const dayOf = (r: Row) => ({ cov: n(r.eod?.calc?.covers) ?? n(r.covers), sales: n(r.eod?.calc?.total) ?? n(r.total_sales) });
    const avg = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

    // Public holidays (this year and last), for the venue's state.
    let hol = new Map<string, string>();
    try {
      const st = String(v?.state ?? '').trim().toUpperCase();
      const years = [...new Set([week.slice(0, 4), addDays(week, 6).slice(0, 4), ly(week).slice(0, 4)])];
      const lists = await Promise.all(years.map((y) => fetch(`https://date.nager.at/api/v3/PublicHolidays/${y}/AU`).then((r) => (r.ok ? r.json() : [])).catch(() => [])));
      hol = new Map((lists.flat() as Array<{ date: string; localName: string; counties: string[] | null }>)
        .filter((h) => !h.counties || !st || h.counties.includes(`AU-${st}`)).map((h) => [h.date, h.localName]));
    } catch { /* no holidays: fine */ }

    // Last 4 of each weekday: per-service covers and $ per head, and day covers.
    const acc: Record<string, { cov: number[][]; sph: number[][] }> = {};
    for (const s of services) acc[s] = { cov: DAYS.map(() => []), sph: DAYS.map(() => []) };
    const day4: number[][] = DAYS.map(() => []);
    for (const r of (rows ?? []) as Row[]) {
      if (hol.has(r.trade_date)) continue; // a holiday isn't a normal day
      const i = (new Date(`${r.trade_date}T12:00:00`).getDay() + 6) % 7;
      const d = dayOf(r);
      if (d.cov) day4[i].push(d.cov);
      const per = r.eod?.calc?.services ?? {};
      const hasSplit = services.some((s) => n(per[s]?.covers));
      for (const s of services) {
        let cov = n(per[s]?.covers), sales = n(per[s]?.sales);
        if (!hasSplit && s === services[services.length - 1]) { cov = d.cov; sales = d.sales; }
        if (cov && cov > 0) { acc[s].cov[i].push(cov); if (sales) acc[s].sph[i].push(sales / cov); }
      }
    }
    // Each service's usual share of the day's covers and its usual $ per head, across all weekdays:
    // used for a weekday that has no history for that service.
    const usualShare: Record<string, number> = {}, usualSph: Record<string, number> = {};
    {
      const totals = services.map((s) => acc[s].cov.flat().reduce((a, b) => a + b, 0));
      const all = totals.reduce((a, b) => a + b, 0);
      services.forEach((s, k) => {
        if (all > 0 && acc[s].cov.filter((d) => d.length).length >= 2) usualShare[s] = totals[k] / all;
        const sp = avg(acc[s].sph.flat()); if (sp != null) usualSph[s] = sp;
      });
    }
    // Year-on-year trend: last 4 weeks vs the same 4 weeks last year.
    const lyMap = new Map(((lyRows ?? []) as Row[]).map((r) => [r.trade_date, dayOf(r)]));
    const recent = ((rows ?? []) as Row[]).filter((r) => !hol.has(r.trade_date)).map((r) => ({ d: r.trade_date, c: dayOf(r).cov }));
    let nowSum = 0, thenSum = 0;
    for (const x of recent) { const t = lyMap.get(ly(x.d))?.cov; if (x.c && t) { nowSum += x.c; thenSum += t; } }
    const trend = thenSum > 0 ? Math.min(2, Math.max(0.5, nowSum / thenSum)) : null;

    const notes: string[] = [];
    let any = false;
    const next: Data = { ...data, services: { ...data.services } };
    for (const s of services) next.services[s] = { ...next.services[s], covers: [...next.services[s].covers], sph: [...next.services[s].sph] };
    for (let i = 0; i < 7; i++) {
      const date = addDays(week, i);
      const base4 = avg(day4[i]);
      const lyDay = lyMap.get(ly(date))?.cov ?? null;
      const lyAdj = lyDay && trend ? lyDay * trend : lyDay;
      let target = base4 != null && lyAdj != null ? 0.7 * base4 + 0.3 * lyAdj : base4 ?? lyAdj;
      let why = `${DAYS[i]}: ${base4 != null ? `last ${day4[i].length} ${DAYS[i]}s avg ${Math.round(base4)}` : 'no recent data'}${lyAdj != null ? ` · last year ${Math.round(lyDay!)}${trend ? ` × ${trend.toFixed(2)} trend` : ''}` : ''}`;
      const h = hol.get(date);
      if (h) {
        // Same holiday last year (it may fall on another weekday): use that day if we have it.
        const lyHolDate = [...hol.entries()].find(([d, nm]) => nm === h && d < date && d.slice(0, 4) !== date.slice(0, 4))?.[0];
        const lyHol = lyHolDate ? lyMap.get(lyHolDate)?.cov ?? null : null;
        if (lyHol) { target = lyHol * (trend ?? 1); why += ` · ${h}: last year's ${h} ${lyHol} covers used`; }
        else why += ` · ${h} (public holiday): check this one`;
      }
      if (target == null) continue;
      // Split the day across services like the last 4 weeks did. A service with no history on this
      // weekday (e.g. happy hour wasn't split out on Mondays) takes its usual share from the other days,
      // and a service with covers but no sales history takes its usual $ per head from the other days.
      const svcAvg = services.map((s) => avg(acc[s].cov[i]));
      const known = svcAvg.reduce<number>((a, b) => a + (b ?? 0), 0);
      const filled: string[] = [];
      const shares = services.map((s, k) => {
        if (svcAvg[k] != null) return known > 0 ? (svcAvg[k]! / known) * (1 - services.reduce((a, x, j) => a + (svcAvg[j] == null ? usualShare[x] ?? 0 : 0), 0)) : 0;
        const u = usualShare[s] ?? 0;
        if (u > 0) filled.push(s);
        return u;
      });
      const totShare = shares.reduce((a, b) => a + b, 0);
      services.forEach((s, k) => {
        const share = totShare > 0 ? shares[k] / totShare : k === services.length - 1 ? 1 : 0;
        if (share > 0) next.services[s].covers[i] = Math.round(target! * share);
        const p = avg(acc[s].sph[i]) ?? usualSph[s] ?? null;
        if (p != null) next.services[s].sph[i] = Math.round(p * 100) / 100;
        if (avg(acc[s].sph[i]) == null && usualSph[s] != null && share > 0 && !filled.includes(s)) filled.push(`${s} $/head`);
      });
      if (filled.length) why += ` · no ${DAYS[i]} history for ${filled.join(', ')}: used the usual from other days`;
      any = true;
      notes.push(why);
    }
    if (!any) { setErr('No submitted nights in the last 4 weeks or the same time last year to work from.'); return; }
    setData(next); setDirty(true); setSaving('idle'); setWhy(notes);
  };

  const save = async () => {
    setSaving('saving'); setErr(null);
    const { error } = await supabase.from('venue_forecasts').upsert({ venue_id: venueId, week_start: week, data, updated_by: userId ?? null, updated_at: new Date().toISOString() });
    if (error) { setSaving('idle'); setErr(/venue_forecasts|schema cache/.test(error.message) ? 'Forecasts need the latest database update (20261028070000_weekly_reports.sql).' : error.message); return; }
    setSaving('saved'); setDirty(false);
  };

  const cell = 'w-full h-9 rounded-lg px-2 text-[13px] text-right tabular-nums';
  const inStyle = { background: c.iconBg, color: c.text, border: `1px solid ${c.border}` };
  const label = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  const go = (k: number) => { if (dirty && !window.confirm('You have unsaved changes. Leave this week?')) return; setWeek((w) => addDays(w, 7 * k)); };
  const target = data.labourTarget ?? null;

  const body = (
    <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold" style={{ color: c.text }}>Forecast</h2>
                <p className="text-sm mt-0.5" style={{ color: c.textDim }}>Bookings, covers and spend per head. Used in the weekly report.</p>
              </div>
              {!inline && <button onClick={() => { if (!dirty || window.confirm('Close without saving?')) onClose(); }} className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: c.iconBg, color: c.textDim }} aria-label="Close"><X size={16} /></button>}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => go(-1)} className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: c.iconBg, color: c.text }} aria-label="Previous week"><ChevronLeft size={16} /></button>
              <span className="text-sm font-semibold px-1" style={{ color: c.text }}>{label(week)} – {label(addDays(week, 6))}</span>
              <button onClick={() => go(1)} className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: c.iconBg, color: c.text }} aria-label="Next week"><ChevronRight size={16} /></button>
              <div className="flex-1" />
              <button onClick={() => void fillFromHistory()} className="h-9 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5" style={{ background: c.iconBg, color: c.text }}>
                <Wand2 size={13} /> Suggest from history
              </button>
              <button onClick={() => void save()} disabled={saving === 'saving' || (!dirty && saving !== 'idle')}
                className="h-9 px-4 rounded-xl text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60" style={{ background: c.accent, color: '#fff' }}>
                {saving === 'saving' ? <Loader2 size={13} className="animate-spin" /> : saving === 'saved' ? <Check size={13} /> : null}
                {saving === 'saved' ? 'Saved' : 'Save'}
              </button>
            </div>
            {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}
            {why.length > 0 && (
              <details className="text-[12px] rounded-xl px-3 py-2" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.textDim }}>
                <summary className="cursor-pointer font-semibold" style={{ color: c.text }}>How this was worked out</summary>
                <p className="mt-1.5">Last 4 of the same weekday (70%) and the same weekday last year, adjusted for this year's trend (30%). Public holidays use last year's same holiday. Bookings, events and weather aren't included yet: adjust by hand.</p>
                <ul className="mt-1.5 space-y-0.5">{why.map((w) => <li key={w}>{w}</li>)}</ul>
              </details>
            )}

            {loading ? <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div> : <>
              {services.map((s) => (
                <div key={s} className="rounded-2xl p-3 sm:p-4 overflow-x-auto" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                  <table className="w-full min-w-[640px] border-separate" style={{ borderSpacing: '4px 4px' }}>
                    <thead>
                      <tr>
                        <th className="text-left text-sm font-semibold w-28" style={{ color: c.text }}>{s}</th>
                        {DAYS.map((d, i) => <th key={d} className="text-[11px] font-semibold text-right pr-2" style={{ color: c.textDim }}>{d} {new Date(`${addDays(week, i)}T12:00:00`).getDate()}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {([['bookings', 'Bookings'], ['covers', 'Covers'], ['sph', '$ / head']] as const).map(([k, l]) => (
                        <tr key={k}>
                          <td className="text-[12px] font-medium" style={{ color: c.textDim }}>{l}</td>
                          {DAYS.map((_, i) => (
                            <td key={i}><input type="number" inputMode="decimal" min={0} step={k === 'sph' ? 0.5 : 1} value={data.services[s]?.[k][i] ?? ''}
                              onChange={(e) => set(s, k, i, e.target.value)} className={cell} style={inStyle} /></td>
                          ))}
                        </tr>
                      ))}
                      <tr>
                        <td className="text-[12px] font-semibold" style={{ color: c.text }}>Sales</td>
                        {DAYS.map((_, i) => { const r = rev(s, i); return <td key={i} className="text-[13px] font-semibold text-right pr-2 tabular-nums" style={{ color: r ? c.text : c.textFaint }}>{r ? formatMoney(r) : '—'}</td>; })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              ))}

              <div className="rounded-2xl p-3 sm:p-4 overflow-x-auto" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                <table className="w-full min-w-[640px] border-separate" style={{ borderSpacing: '4px 6px' }}>
                  <thead>
                    <tr>
                      <th className="text-left text-sm font-semibold w-28" style={{ color: c.text }}>Day total</th>
                      {DAYS.map((d) => <th key={d} className="text-[11px] font-semibold text-right pr-2" style={{ color: c.textDim }}>{d}</th>)}
                    </tr>
                  </thead>
                  <tbody className="text-[13px] tabular-nums">
                    <tr><td style={{ color: c.textDim }}>Sales (inc GST)</td>{dayRev.map((r, i) => <td key={i} className="text-right pr-2 font-semibold" style={{ color: c.text }}>{r ? formatMoney(r) : '—'}</td>)}</tr>
                    <tr><td style={{ color: c.textDim }} title="Includes draft shifts not published yet">Rostered labour</td>{sched.map((v, i) => <td key={i} className="text-right pr-2" style={{ color: c.text }}>{v ? formatMoney(v) : '—'}</td>)}</tr>
                    <tr><td style={{ color: c.textDim }}>Labour %</td>{sched.map((v, i) => { const p = labourPct(v, dayRev[i]); const over = p != null && target != null && p > target; return <td key={i} className="text-right pr-2 font-semibold" style={{ color: p == null ? c.textFaint : over ? c.danger : c.accentSoft }}>{p == null ? '—' : `${p}%`}</td>; })}</tr>
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ['Forecast sales', weekRev ? formatMoney(weekRev) : '—'],
                  ['Forecast covers', weekCovers ? String(weekCovers) : '—'],
                  ['Rostered labour (incl. drafts)', schedTotal ? formatMoney(schedTotal) : 'No shifts yet'],
                  ['Labour %', labourPct(schedTotal, weekRev) != null ? `${labourPct(schedTotal, weekRev)}%` : '—'],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-2xl p-3.5" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: c.textDim }}>{l}</p>
                    <p className="text-lg font-semibold mt-1 tabular-nums" style={{ color: c.text }}>{v}</p>
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm" style={{ color: c.textDim }}>
                Labour target
                <input type="number" min={0} max={100} step={0.5} value={target ?? ''} placeholder="e.g. 28"
                  onChange={(e) => { setData((d) => ({ ...d, labourTarget: e.target.value === '' ? null : +e.target.value })); setDirty(true); setSaving('idle'); }}
                  className="w-20 h-9 rounded-lg px-2 text-right" style={inStyle} />
                % of sales ex GST — days over it show in red.
              </label>
            </>}
    </>
  );
  if (inline) return <div className="space-y-5">{body}</div>;
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="fixed inset-0 flex items-end sm:items-center justify-center" style={{ zIndex: 60, background: 'rgba(0,0,0,0.45)' }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { if (!dirty || window.confirm('Close without saving?')) onClose(); }}>
          <motion.div onClick={(e) => e.stopPropagation()} initial={{ y: 30 }} animate={{ y: 0 }} exit={{ y: 30 }}
            className="w-full sm:max-w-5xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 space-y-5" style={{ background: c.bg }}>
            {body}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
